/**
 * WHICH SCREEN A FROM-AIRPORT RIDE BELONGS ON — the one rule, read by everything that
 * shows a piece of the ride.
 *
 * Two surfaces cover the same ride at different moments:
 *
 *   the arrival guide       the arrivals hall — the flight, then the walk to the booth.
 *                           ⚠️ A MODAL, NOT A PAGE (`WelcomeModal`, raised by `AppLayout`).
 *                           `/:tripId/welcome` was deleted on 2026-09-23.
 *   /:tripId/track-driver   in the car — the driver's position on a map
 *
 * The boundary below is what keeps them from both claiming one ride: the guide is offered
 * while the ride has not started, the map takes over once it has. It is defined here once
 * rather than as two conditions that happen to be opposites today.
 *
 * ⚠️ THE BOUNDARY IS `InProgress` — the passenger is in the car. Before it, whatever else
 * is happening (a driver assigned, a driver EnRoute at the kerb), the passenger is still
 * walking and the arrival guide is the screen that helps. From it on, the walk is over and
 * the map is.
 *
 * ⚠️ AND A FINISHED RIDE BELONGS TO NEITHER. `Completed` has no driver to track and no
 * booth to walk to; it belongs on its own trip page. `Cancelled` and `NoShow` likewise —
 * left on either screen they would show a live map of a ride that is not happening.
 */

/**
 * Over, one way or another. All three go to the trip's own page: it renders itself
 * according to status, so a completed ride and a cancelled one each explain themselves
 * there — and the passenger keeps the ride they were following rather than being dropped
 * on a list of every ride they have ever taken.
 *
 * It is also where `TrackDriver` has always sent a finished ride, so the status-driven
 * redirect below and that page's own phase-driven one agree rather than compete.
 */
const FINISHED_STATUSES = new Set(['Completed', 'Cancelled', 'NoShow'])

export function isTripFinished(trip) {
    return FINISHED_STATUSES.has(String(trip?.status || ''))
}

/**
 * Still on the way TO the passenger — anything live that is not yet `InProgress`.
 *
 * ⚠️ NOT `!isPassengerOnboard(...)`. An unknown or missing status must not read as "before
 * boarding" and send someone to the arrivals hall on the strength of a field that failed to
 * load; this answers true only for a status we recognise as live and pre-boarding.
 */
const PRE_ONBOARD_STATUSES = new Set([
    'Requested',
    'Broadcasted',
    'Accepted',
    'Assigned',
    'EnRoute',
])

export function isBeforeOnboard(trip) {
    return PRE_ONBOARD_STATUSES.has(String(trip?.status || ''))
}

/**
 * The trip has not STARTED yet — pre-onboard, or the driver waiting at the pickup
 * (`Arrived`). Wider than `isBeforeOnboard` by that one status, which matters for the
 * "flight has landed" prompt: its window is landing → trip start, and a driver standing
 * at the kerb is still inside it.
 */
export function isBeforeTripStart(trip) {
    return isBeforeOnboard(trip) || String(trip?.status || '') === 'Arrived'
}

/**
 * The passenger is in the car — `InProgress`, or anything the backend adds AFTER it and
 * before the ride is over.
 *
 * ⚠️ DEFINED BY EXCLUSION, and that is the point. Naming `InProgress` explicitly would send
 * any future mid-ride status (a stopover, a waiting state) back to the arrivals hall — the
 * one place a passenger sitting in a moving car must not be sent. Listing the two ENDS and
 * treating everything between them as "under way" fails in the safe direction.
 *
 * An empty status is still no answer: it belongs to neither end, so nothing redirects.
 */
export function isPassengerOnboard(trip) {
    const status = String(trip?.status || '')
    if (!status) return false
    return !PRE_ONBOARD_STATUSES.has(status) && !FINISHED_STATUSES.has(status)
}

/** Where a finished ride goes instead. */
export const tripDetailsPath = (tripId) => `/rides/${tripId}`
export const trackDriverPath = (tripId) => `/${tripId}/track-driver`
/* ⚠️ NO `welcomePath`. There is no URL for the arrival guide any more — it is a modal, and
   the only way in is the plane button in `AppLayout`. */
